import { Button, Form, Spinner } from '@heroui/react'
import { useLookupFile, useUpdateFile } from '@qse/api'
import { queryKeys } from '@qse/api'
import { type ModalComponentProps, ModalData } from '@qse/constants'
import { HeroInput } from '@qse/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Validation function for file name
const validateFileName = (value: string) => {
  if (!value || value.trim() === '') {
    return 'File name cannot be empty'
  }
  if (value.length < 1) {
    return 'File name must be at least 1 character'
  }
  if (value.length > 255) {
    return 'File name cannot exceed 255 characters'
  }

  // Check for invalid characters that are not allowed in file/folder names
  // eslint-disable-next-line no-control-regex
  const invalidChars = /[<>:"/\\|?\x00-\x1f]/g

  if (invalidChars.test(value)) {
    return 'File name contains invalid characters (< > : " / \\ | ? or control characters)'
  }

  // Check for reserved names (Windows)
  const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i

  if (reservedNames.test(value)) {
    return 'File name cannot be a reserved system name'
  }

  // Check for names ending with dot or space
  if (value.endsWith('.') || value.endsWith(' ')) {
    return 'File name cannot end with a dot or space'
  }

  // Check for special directory names
  if (value === '.' || value === '..') {
    return 'File name cannot be "." or ".."'
  }

  // Check for names starting with dot (optional - remove if you want to allow hidden files)
  if (value.startsWith('.')) {
    return 'File name cannot start with a dot'
  }

  return true
}

export default function RenameFile(props: ModalComponentProps) {
  const { setModelActive, onClose, data } = props
  const config: ModalData = data
  const updateFile = useUpdateFile()
  const queryClient = useQueryClient()
  const [fileName, setFileName] = useState<string>('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const currentFileName = (config as any).data.currentFileName || ''

  // Initialize fileName state with current file name
  useEffect(() => {
    setFileName(currentFileName)
  }, [currentFileName])

  // Debounce the file name for API calls (500ms delay)
  const debouncedFileName = useDebounce(fileName, 500)

  // Use useLookupFile hook for real-time conflict detection with debounced value
  const lookupParams = useMemo(() => {
    if (!debouncedFileName.trim()) return null

    // Skip conflict checking if the name hasn't changed from the original
    if (debouncedFileName.trim() === currentFileName) return null

    // Get parent information from config data
    const parentId = (config as any).data.parentId

    const params = {
      name: debouncedFileName.trim(),
      parentId: parentId,
    }

    return params
  }, [debouncedFileName, currentFileName, config])

  const { data: lookupResult, isLoading: isCheckingConflict } = useLookupFile(
    lookupParams || {
      name: '',
      parentId: undefined,
      parentPath: undefined,
    },
  )

  // Invalidate lookup cache when modal opens to ensure fresh checks
  useEffect(() => {
    // Clear any existing lookup cache when modal opens
    queryClient.removeQueries({
      queryKey: queryKeys.files.lookup('', ''),
    })
  }, [queryClient])

  // Force refetch when debounced value changes to ensure fresh data
  useEffect(() => {
    if (debouncedFileName.trim() && lookupParams) {
      // Invalidate the specific lookup query to force a fresh request
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.lookup(
          debouncedFileName.trim(),
          (config as any).data.parentId || (config as any).data.parentPath || '',
        ),
      })
    }
  }, [debouncedFileName, lookupParams, queryClient, config])

  // Check for naming conflicts and validate file name
  useEffect(() => {
    if (!debouncedFileName.trim()) {
      setValidationError(null)

      return
    }

    // First check for naming conflicts
    if (
      lookupResult?.file &&
      debouncedFileName.trim() &&
      debouncedFileName.trim() !== currentFileName
    ) {
      setValidationError('A file or folder with this name already exists in this directory')

      return
    }

    // Then validate the file name format
    const validationResult = validateFileName(debouncedFileName)

    if (validationResult !== true) {
      setValidationError(validationResult)
    } else {
      setValidationError(null)
    }
  }, [lookupResult, debouncedFileName, currentFileName])

  // Validate file name on change
  const handleFileNameChange = (value: string) => {
    setFileName(value)

    // Validate the file name
    const validationResult = validateFileName(value)

    if (validationResult !== true) {
      setValidationError(validationResult)
    } else {
      setValidationError(null)
    }
  }

  // Check if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    // If user hasn't typed anything, button is disabled
    if (!fileName.trim()) {
      return true
    }

    // If file name hasn't changed, button is disabled
    if (fileName.trim() === currentFileName) {
      return true
    }

    // If there are validation errors, button is disabled
    if (validationError) {
      return true
    }

    // If update operation is pending, button is disabled
    if (updateFile.isPending) {
      return true
    }

    // Check if fileName has changed AND we have lookup API result
    const hasFileNameChanged = fileName.trim() !== currentFileName
    const hasLookupResult = lookupResult !== undefined

    if (hasFileNameChanged && hasLookupResult) {
      // If still checking for conflicts, button is disabled
      if (isCheckingConflict) {
        return true
      }

      // If a file exists (conflict), disable save button
      if (lookupResult?.file) {
        return true
      }

      // If no file exists (no conflict), enable save button
      return false
    }

    // If fileName hasn't changed OR we don't have lookup result, button is disabled
    return true
  }, [
    fileName,
    currentFileName,
    updateFile.isPending,
    validationError,
    isCheckingConflict,
    lookupResult,
  ])

  // Auto-focus and select input when component mounts (only once)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      // Use setTimeout to ensure the value is set before selecting
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select()
        }
      }, 100)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check for validation errors
    if (validationError) {
      toast.error(validationError)

      return
    }

    // Check if file name has changed
    if (fileName.trim() === currentFileName) {
      toast.error('File name has not changed')

      return
    }

    try {
      const fileId = (config as any).data.fileIds?.[0]

      if (!fileId) {
        toast.error('No file selected for renaming')

        return
      }

      await updateFile.mutateAsync({
        data: { name: fileName.trim() },
        params: { id: fileId },
      })

      toast.success('File renamed successfully')
      setModelActive(undefined)
      onClose()
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status
      const message =
        err?.message ?? err?.response?.data?.message ?? 'Failed to rename file. Please try again.'

      if (status === 409) {
        // Show toast error message for 409 conflicts
        toast.error(message)
        // Focus the input field after showing the error
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus()
          }
        }, 100)

        return
      }
      toast.error(message)
      onClose()
    }
  }

  return (
    <div>
      <Form
        className="flex flex-col gap-5 py-5"
        validationBehavior="native"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-2 w-full">
          <HeroInput
            ref={inputRef}
            isRequired
            classNameContainer="w-full"
            control="text"
            description={fileName.length > 0 ? `${fileName.length}/255 characters` : undefined}
            errorMessage={validationError}
            isInvalid={!!validationError}
            label="New File Name"
            maxLength={255}
            name="fileName"
            placeholder="Enter new file name"
            type="text"
            value={fileName}
            endContent={
              <>
                <div className="flex items-center gap-2">
                  {isCheckingConflict && debouncedFileName.trim() && (
                    <Spinner color="primary" size="sm" />
                  )}
                </div>
              </>
            }
            onChange={handleFileNameChange}
          />
        </div>
        <div className="flex justify-end gap-3 w-full">
          <Button radius="sm" type="button" onPress={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            isDisabled={isSaveDisabled}
            isLoading={updateFile.isPending}
            radius="sm"
            type="submit"
          >
            Save
          </Button>
        </div>
      </Form>
    </div>
  )
}
